import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Command } from "cmdk";
import {
  Search, Calendar, FileText, TrendingUp, User,
  Ship, LogOut, Sun, ArrowRight, CalendarDays
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_API_URL}/api`;

const heading = { fontFamily: "'Cabinet Grotesk', 'Outfit', sans-serif" };

export const CommandPalette = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const navigate = useNavigate();

  // Toggle with Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load bookings + boats when palette opens for the first time
  useEffect(() => {
    if (!open || bookings.length > 0) return;
    const token = localStorage.getItem("nautiqo_token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/bookings`, { headers })
      .then(res => setBookings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
    axios.get(`${API}/boats`, { headers })
      .then(res => setBoats(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [open, bookings.length]);

  const go = useCallback((path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  }, [navigate]);

  const todayStr = format(new Date(), "yyyy-MM-dd");

  // Filter bookings (max 8) by code or customer name
  const filteredBookings = useMemo(() => {
    if (!query) return bookings.slice(0, 5);
    const q = query.toLowerCase();
    return bookings.filter(b =>
      (b.booking_code || "").toLowerCase().includes(q) ||
      (b.customer_name || "").toLowerCase().includes(q) ||
      (b.customer_email || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [bookings, query]);

  const filteredBoats = useMemo(() => {
    if (!query) return boats.slice(0, 4);
    const q = query.toLowerCase();
    return boats.filter(b =>
      (b.name || "").toLowerCase().includes(q) ||
      (b.model || "").toLowerCase().includes(q)
    ).slice(0, 6);
  }, [boats, query]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-slate-400 transition"
        data-testid="cmdk-trigger"
        aria-label="Apri ricerca rapida"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Cerca…</span>
        <kbd className="ml-2 px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">⌘K</kbd>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      data-testid="cmdk-overlay"
    >
      <div className="w-full max-w-xl bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden" data-testid="cmdk-panel">
        <Command label="Comando rapido" loop>
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
            <Search className="w-4 h-4 text-slate-500" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Cerca prenotazione, barca o azione…"
              className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm"
              data-testid="cmdk-input"
            />
            <kbd className="text-[10px] text-slate-500 px-1.5 py-0.5 bg-white/5 rounded font-mono">ESC</kbd>
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-2">
            <Command.Empty className="py-8 text-center text-sm text-slate-500" data-testid="cmdk-empty">
              Nessun risultato. Prova "fatturato", "oggi" o un codice prenotazione.
            </Command.Empty>

            {/* Quick actions */}
            <Command.Group heading="Azioni" className="text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 pt-2 pb-1">
              <CmdItem onSelect={() => go(`/admin?date=${todayStr}`)} icon={<Sun className="w-4 h-4 text-amber-400" />} testid="cmdk-action-today">
                Vai a oggi <span className="ml-auto text-xs text-slate-500">{format(new Date(), "dd MMM")}</span>
              </CmdItem>
              <CmdItem onSelect={() => go("/admin")} icon={<Calendar className="w-4 h-4 text-blue-400" />} testid="cmdk-action-calendar">
                Apri Calendario
              </CmdItem>
              <CmdItem onSelect={() => go("/admin/prenotazioni")} icon={<FileText className="w-4 h-4 text-violet-400" />} testid="cmdk-action-bookings">
                Apri Prenotazioni
              </CmdItem>
              <CmdItem onSelect={() => go("/admin/fatturato")} icon={<TrendingUp className="w-4 h-4 text-emerald-400" />} testid="cmdk-action-revenue">
                Apri Fatturato
              </CmdItem>
              <CmdItem onSelect={() => go("/admin/account")} icon={<User className="w-4 h-4 text-slate-300" />} testid="cmdk-action-account">
                Apri Account
              </CmdItem>
              <CmdItem onSelect={onLogout} icon={<LogOut className="w-4 h-4 text-red-400" />} testid="cmdk-action-logout">
                Esci
              </CmdItem>
            </Command.Group>

            {/* Bookings */}
            {filteredBookings.length > 0 && (
              <Command.Group heading="Prenotazioni" className="text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 pt-3 pb-1">
                {filteredBookings.map((b) => (
                  <CmdItem
                    key={b.id || b.booking_code}
                    onSelect={() => go("/admin/prenotazioni")}
                    icon={<CalendarDays className="w-4 h-4 text-blue-400" />}
                    testid={`cmdk-booking-${b.booking_code}`}
                  >
                    <span className="font-semibold">{b.booking_code || "—"}</span>
                    <span className="text-slate-400 ml-2 truncate">{b.customer_name || b.customer_email || ""}</span>
                    <span className="ml-auto text-xs text-slate-500">{b.booking_date}</span>
                  </CmdItem>
                ))}
              </Command.Group>
            )}

            {/* Boats */}
            {filteredBoats.length > 0 && (
              <Command.Group heading="Barche" className="text-[10px] uppercase tracking-wider text-slate-500 font-bold px-2 pt-3 pb-1">
                {filteredBoats.map((b) => (
                  <CmdItem
                    key={b.id}
                    onSelect={() => go("/admin")}
                    icon={<Ship className="w-4 h-4 text-emerald-400" />}
                    testid={`cmdk-boat-${b.id}`}
                  >
                    <span className="font-semibold">{b.name || "—"}</span>
                    {b.model && <span className="text-slate-400 ml-2 text-xs">{b.model}</span>}
                  </CmdItem>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className="border-t border-white/10 px-4 py-2 flex items-center justify-between text-[11px] text-slate-500">
            <span style={heading} className="font-semibold tracking-wide">Nautiqo</span>
            <span className="flex items-center gap-3">
              <span><kbd className="px-1 py-0.5 bg-white/5 rounded font-mono">↑↓</kbd> naviga</span>
              <span><kbd className="px-1 py-0.5 bg-white/5 rounded font-mono">↵</kbd> apri</span>
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
};

const CmdItem = ({ children, onSelect, icon, testid }) => (
  <Command.Item
    onSelect={onSelect}
    className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-sm text-slate-200 data-[selected=true]:bg-white/8 data-[selected=true]:text-white transition"
    data-testid={testid}
  >
    {icon}
    <div className="flex-1 flex items-center gap-1 truncate">{children}</div>
    <ArrowRight className="w-3 h-3 text-slate-600 opacity-0 group-data-[selected=true]:opacity-100" />
  </Command.Item>
);
